import React, {
  useState,
  useEffect,
  useContext,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import ModalDialogContext from "../../../../../../../Context/ModalDialog/ModalDialogContext";
import {
  NoAccessScreen,
  LoadingIndicator,
  LongText,
  LightTooltip,
} from "../../../../../../../Component";
import { styles } from "./styles";
import {
  Button,
  Card,
  CardActions,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from "@material-ui/core";
import Icon from "@jll/icon";
import CheckBox from "../../../../../../../Component/Forms/Checkbox";
import SearchableSelect from "../../../../../../../Component/Forms/SearchableSelect";
import CurrencyInput from "../../../../../../../Component/Forms/CurrencyInput";
import { getDataAugmentation } from "../../../../../../../API/Entity";
import { recordTypes } from "../../../../../../../API/config";
import {
  getAttributeValue,
  formatDate,
  getAttributeUnitValue,
  getFormattedCurrenyValue,
} from "../../../../../../../Utilities/Functions";

const AttributesTable = (props, ref) => {
  const {
    entity,
    sources,
    availableSources,
    listOptions,
    hasRecordPermission,
    classes,
    onAttributeChange,
    resetAttribute,
    onUnitChange,
    saveRecord,
    reloadParent,
  } = props;

  const { showErrorMessage, showConfirmation } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [sourceNames, setSourceNames] = useState([]);
  const [augmentableAttributes, setAugmentableAttributes] = useState([]);
  const [hasValidAttr, setHasValidAttr] = useState({});
  const [modifiedAttributes, setModifiedAttributes] = useState({});
  const [hasValidUnit, setHasValidUnit] = useState({});
  const [modifiedUnits, setModifiedUnits] = useState({});
  const [isUpdateOperating, setIsUpdateOperating] = useState(false);
  const [changesReady, setChangesReady] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  const resetStates = () => {
    setSourceNames([]);
    setAugmentableAttributes([]);
    setHasValidAttr({});
    setModifiedAttributes({});
    setHasValidUnit({});
    setModifiedUnits({});
  };

  useImperativeHandle(
    ref,
    () => ({
      hasUnsavedChanges: () => {
        return (
          augmentableAttributes.filter((a) => a.selectedSource !== "current")
            .length > 0
        );
      },
      discardChanges: () => {
        const changed = augmentableAttributes.filter(
          (a) => a.selectedSource !== "current",
        );
        changed.forEach((ca) => {
          resetAttribute(ca.attributeId, `${ca.currentValue}`);
        });
        resetStates();
      },
    }),
    [augmentableAttributes],
  );

  useEffect(() => {
    const setAugmentationData = (augmentationData) => {
      const { attributes: entityAttributes = [] } = entity || {};

      const augmentableAttributes = [];
      Object.keys(augmentationData).forEach((sourceName) => {
        const { attributes = [] } = augmentationData[sourceName];
        attributes.forEach((a) => {
          let aa = augmentableAttributes.find(
            (aa) => aa.attributeId === a.attributeId,
          );
          if (!aa) {
            const ea = entityAttributes.find(
              (attr) => attr.id == a.attributeId,
            );
            if (ea) {
              const av = getAttributeValue(ea);
              aa = {
                attributeId: a.attributeId,
                name: ea.name,
                dataType: ea.data_type,
                sourceValues: {
                  current: av,
                },
                sourceUnits: {
                  current: getAttributeUnitValue(ea),
                },
                currentValue: av,
                selectedSource: "current",
                listId: ea.list_id,
              };
              augmentableAttributes.push(aa);
            }
          }
          aa.sourceValues[sourceName] = a.sourceValue;
          if ("unit" in a) {
            aa.sourceUnits[sourceName] = a.unit;
          }
        });
        setSourceNames((oldVal) => [...oldVal, sourceName]);
      });

      setAugmentableAttributes(augmentableAttributes);
    };

    const loadAvailableAttributes = async () => {
      setLoading(true);
      try {
        const response = await getDataAugmentation(
          entity.id,
          availableSources
            .filter((s) => sources.map(({ label }) => label).includes(s.label))
            .map((s) => s.value),
          "attributes",
        );
        const { data = {} } = response;
        const { sourcesData = {}, errors } = data || {};
        if (errors && errors.length > 0) {
          throw new Error(errors.join("\n"));
        }
        setAugmentationData(sourcesData);
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    };

    const setPermissions = async () => {
      const { permissions = {} } = entity;

      const canUpdateRecord = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name,
      );
      setHasAccess(canUpdateRecord);

      if (canUpdateRecord) {
        await loadAvailableAttributes();
      }
    };

    if (sources) {
      resetStates();
      setPermissions();
    }
  }, [sources]);

  useEffect(() => {
    const validateListOptions = (attributes, allSources, allOptions) => {
      attributes.forEach((attr) => {
        if (["L", "CL", "EL", "RL"].includes(attr.dataType)) {
          let options = allOptions[attr.listId || attr.dataType];
          allSources.forEach((sourceName) => {
            if (attr.sourceValues[sourceName]) {
              let match =
                options.filter((o) => o.value === attr.sourceValues[sourceName])
                  .length != 0;
              setHasValidAttr((oldVal) => ({
                ...oldVal,
                [`${attr.attributeId}-${sourceName}`]: match,
              }));
            }
          });
        } else if (attr.dataType === "CR") {
          let options = allOptions[attr.listId || attr.dataType];
          allSources.forEach((sourceName) => {
            if (attr.sourceUnits[sourceName]) {
              let match =
                options.filter((o) => o.value === attr.sourceUnits[sourceName])
                  .length != 0;
              setHasValidUnit((oldVal) => ({
                ...oldVal,
                [`${attr.attributeId}-${sourceName}`]: match,
              }));

              if (Number.isNaN(Number(attr.sourceValues[sourceName]))) {
                setHasValidAttr((oldVal) => ({
                  ...oldVal,
                  [`${attr.attributeId}-${sourceName}`]: false,
                }));
              }
            }
          });
        }
      });
    };

    validateListOptions(augmentableAttributes, sourceNames, listOptions);
  }, [listOptions, augmentableAttributes, sourceNames]);

  useEffect(() => {
    if (changesReady) {
      setChangesReady(false);
      showConfirmation({
        message: t("messages.record.augmentation.updateConfirm"),
        onPressYes: async (event) => {
          setIsUpdateOperating(true);
          await saveRecord(recordTypes.record.actions.augmentData.name);
          setIsUpdateOperating(false);
          reloadParent(true);
        },
      });
    }
  }, [changesReady]);

  const saveAttrChanges = () => {
    const changed = augmentableAttributes.filter(
      (a) => a.selectedSource !== "current",
    );
    if (changed.length > 0) {
      changed.forEach((c) => {
        if (c.sourceUnits[c.selectedSource]) {
          let unitValue =
            modifiedUnits[c.attributeId] &&
            modifiedUnits[c.attributeId][c.selectedSource]
              ? modifiedUnits[c.attributeId][c.selectedSource]
              : c.sourceUnits[c.selectedSource];
          onUnitChange(c.attributeId, unitValue);
        }
      });
      setChangesReady(true);
    }
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "30vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderButtonLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "20px",
        }}
      >
        <LoadingIndicator size={20} />
      </div>
    );
  };

  const handleUnitDropdownValueUpdate = (newVal, sourceName, attributeId) => {
    setModifiedUnits((oldVal) => ({
      ...oldVal,
      [attributeId]: {
        [sourceName]: (newVal && newVal.value) || newVal,
      },
    }));

    setHasValidUnit((oldVal) => ({
      ...oldVal,
      [`${attributeId}-${sourceName}`]: !!newVal,
    }));
  };

  const handleDropdownValueUpdate = (newVal, sourceName, attributeId) => {
    setModifiedAttributes((oldVal) => ({
      ...oldVal,
      [attributeId]: {
        [sourceName]: (newVal && newVal.value) || newVal,
      },
    }));

    setHasValidAttr((oldVal) => ({
      ...oldVal,
      [`${attributeId}-${sourceName}`]: !!newVal,
    }));
  };

  const renderAttrValue = (attr) => {
    if (!attr.currentValue) {
      return "";
    }
    switch (attr.dataType) {
      case "D":
        return formatDate(attr.currentValue);
      case "CR":
        return `${attr.sourceUnits["current"] || ""} ${getFormattedCurrenyValue(
          attr.currentValue,
        )}`;
      case "L":
      case "RL":
      case "CL":
      case "EL": {
        let opt = (
          (listOptions || {})[attr.listId || attr.dataType] || []
        ).filter((i) => i.value === attr.currentValue);
        if (opt && opt.length > 0) {
          return opt[0].label;
        }
        return "";
      }
      default:
        return attr.currentValue || "";
    }
  };

  const renderSourceAttrValue = (attr, sourceName) => {
    if (attr.dataType === "CR") {
      if (
        (attr.sourceUnits[sourceName] &&
          !hasValidUnit[`${attr.attributeId}-${sourceName}`] &&
          (!modifiedUnits[attr.attributeId] ||
            !modifiedUnits[attr.attributeId][sourceName])) ||
        (attr.sourceValues[sourceName] &&
          !getFormattedCurrenyValue(attr.sourceValues[sourceName]))
      ) {
        return (
          <>
            <Grid container spacing={0}>
              <Grid
                item
                xs={10}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Typography
                  component="p"
                  variant={"caption"}
                  style={{ textAlign: "left", fontWeight: "bold" }}
                >
                  {`${attr.sourceUnits[sourceName] || ""} ${
                    attr.sourceValues[sourceName]
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={2} style={{ textAlign: "right" }}>
                <LightTooltip
                  title={
                    <span>
                      {t("messages.record.augmentation.unrecognizedValues")}
                    </span>
                  }
                  placement="left-start"
                  style={{ padding: "0.5rem" }}
                >
                  <div>
                    <Icon className="fa fa-question" style={{ fontSize: 12 }} />
                  </div>
                </LightTooltip>
              </Grid>
            </Grid>
            <div style={{ marginTop: "-10px", paddingBottom: "5px" }}>
              <CurrencyInput
                initialValue={attr.sourceValues[sourceName]}
                initialUnitValue={attr.sourceUnits[sourceName]}
                label=""
                labelIcon=""
                disabled={
                  Number.isNaN(Number(attr.sourceValues[sourceName]))
                    ? false
                    : true
                }
                required={false}
                currencies={
                  (listOptions || {})[attr.listId || attr.dataType] || []
                }
                onValueChange={(value) => {
                  handleDropdownValueUpdate(
                    value,
                    sourceName,
                    attr.attributeId,
                  );
                }}
                onUnitCHange={(value) => {
                  handleUnitDropdownValueUpdate(
                    value,
                    sourceName,
                    attr.attributeId,
                  );
                }}
              />
            </div>
          </>
        );
      } else if (
        attr.sourceUnits[sourceName] &&
        hasValidUnit[`${attr.attributeId}-${sourceName}`] &&
        modifiedUnits[attr.attributeId] &&
        modifiedUnits[attr.attributeId][sourceName]
      ) {
        return (
          <>
            <Grid container spacing={0}>
              <Grid
                item
                xs={10}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Typography
                  component="p"
                  variant={"caption"}
                  style={{ textAlign: "left", fontWeight: "bold" }}
                >
                  {`${
                    attr.sourceUnits[sourceName] || ""
                  } ${getFormattedCurrenyValue(attr.sourceValues[sourceName])}`}
                </Typography>
              </Grid>
              <Grid item xs={2} style={{ textAlign: "right" }}>
                <LightTooltip
                  title={
                    <span>
                      {t("messages.record.augmentation.unrecognizedValues")}
                    </span>
                  }
                  placement="left-start"
                  style={{ padding: "0.5rem" }}
                >
                  <div>
                    <Icon className="fa fa-question" style={{ fontSize: 12 }} />
                  </div>
                </LightTooltip>
              </Grid>
            </Grid>

            <div style={{ marginTop: "-10px", paddingBottom: "5px" }}>
              <CurrencyInput
                initialValue={attr.sourceValues[sourceName]}
                initialUnitValue={attr.sourceUnits[sourceName]}
                label=""
                labelIcon=""
                disabled={
                  Number.isNaN(Number(attr.sourceValues[sourceName]))
                    ? false
                    : true
                }
                required={false}
                currencies={
                  (listOptions || {})[attr.listId || attr.dataType] || []
                }
                onValueChange={(value) => {
                  handleDropdownValueUpdate(
                    value,
                    sourceName,
                    attr.attributeId,
                  );
                }}
                onUnitCHange={(value) => {
                  handleUnitDropdownValueUpdate(
                    value,
                    sourceName,
                    attr.attributeId,
                  );
                }}
              />
            </div>
          </>
        );
      } else {
        return attr.sourceUnits[sourceName] && attr.sourceValues[sourceName]
          ? `${attr.sourceUnits[sourceName]} ${getFormattedCurrenyValue(
              attr.sourceValues[sourceName],
            )}`
          : "";
      }
    } else if (attr.sourceValues[sourceName]) {
      if (attr.dataType === "D") {
        return formatDate(attr.sourceValues[sourceName]);
      } else if (["L", "CL", "EL", "RL"].includes(attr.dataType)) {
        const options = (listOptions || {})[attr.listId || attr.dataType];
        let match =
          (options || []).filter(
            (o) => o.value === attr.sourceValues[sourceName],
          ).length > 0;
        if (match) {
          return (options || []).filter(
            (i) => i.value === attr.sourceValues[sourceName],
          )[0].label;
        } else {
          let value = null;
          if (
            modifiedAttributes[attr.attributeId] &&
            modifiedAttributes[attr.attributeId][sourceName]
          ) {
            value = options.filter(
              (o) =>
                o.value === modifiedAttributes[attr.attributeId][sourceName],
            )[0];
          }
          return (
            <>
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={10}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Typography
                    component="span"
                    variant={"caption"}
                    style={{ textAlign: "left", fontWeight: "bold" }}
                  >
                    {attr.sourceValues[sourceName]}
                  </Typography>
                </Grid>
                <Grid item xs={2} style={{ textAlign: "right" }}>
                  <LightTooltip
                    title={
                      <span>
                        {t("messages.record.augmentation.unrecognizedValues")}
                      </span>
                    }
                    placement="left-start"
                    style={{ padding: "0.5rem" }}
                  >
                    <div>
                      <Icon
                        className="fa fa-question"
                        style={{ fontSize: 12 }}
                      />
                    </div>
                  </LightTooltip>
                </Grid>
              </Grid>
              <SearchableSelect
                label=""
                labelIcon=""
                options={
                  (listOptions || {})[attr.listId || attr.dataType] || []
                }
                allowFreeTest={false}
                value={value}
                changeHandler={(newVal) => {
                  handleDropdownValueUpdate(
                    newVal,
                    sourceName,
                    attr.attributeId,
                  );
                }}
                disabled={false}
                required={false}
              />
            </>
          );
        }
      } else {
        return attr.sourceValues[sourceName];
      }
    }
  };

  const isUnitCheckboxChecked = (attr, sourceName) => {
    let cond1 = attr.selectedSource === sourceName;
    let cond2 = attr.currentValue === attr.sourceValues[sourceName];
    let cond3 = attr.sourceUnits["current"] === attr.sourceUnits[sourceName];
    return cond1 || (cond2 && cond3);
  };

  const isUnitCheckboxDisabled = (attr, sourceName) => {
    let cond1 = attr.currentValue === attr.sourceValues[sourceName];
    let cond2 = attr.sourceUnits["current"] === attr.sourceUnits[sourceName];
    let cond3 = hasValidUnit[`${attr.attributeId}-${sourceName}`];
    let cond4 = modifiedUnits[attr.attributeId]
      ? modifiedUnits[attr.attributeId][sourceName]
        ? true
        : false
      : true;
    let cond5 =
      hasValidAttr[`${attr.attributeId}-${sourceName}`] != undefined
        ? hasValidAttr[`${attr.attributeId}-${sourceName}`]
        : true;
    let cond6 = modifiedAttributes[attr.attributeId]
      ? modifiedAttributes[attr.attributeId][sourceName]
        ? true
        : false
      : true;

    return (cond1 && cond2) || !(cond3 && cond4 && cond5 && cond6);
  };

  const isCheckboxChecked = (attr, sourceName) => {
    let cond1 = attr.selectedSource === sourceName;
    let cond2 =
      attr.dataType === "D"
        ? formatDate(attr.currentValue) ===
          formatDate(attr.sourceValues[sourceName])
        : attr.currentValue === attr.sourceValues[sourceName];
    return cond1 || cond2;
  };

  const isCheckboxDisabled = (attr, sourceName) => {
    let cond1 = attr.currentValue === attr.sourceValues[sourceName];
    let cond2 = ["L", "CL", "EL", "RL"].includes(attr.dataType);
    let cond3 = !hasValidAttr[`${attr.attributeId}-${sourceName}`];
    let cond4 =
      modifiedAttributes[attr.attributeId] &&
      modifiedAttributes[attr.attributeId][sourceName]
        ? true
        : false;

    return (cond1 || (cond2 && cond3)) && !cond4;
  };

  const selectSource = (checked, attrId, source) => {
    const aa = augmentableAttributes.find((aa) => aa.attributeId === attrId);
    aa.selectedSource = checked ? source : "current";
    const allAugmentableAttributes = augmentableAttributes.map((a) => {
      if (a.attributeId === attrId) {
        return aa;
      }
      return a;
    });

    setAugmentableAttributes(allAugmentableAttributes);

    if (checked) {
      let value =
        typeof aa.sourceValues[source] === "number"
          ? `${aa.sourceValues[source]}`
          : aa.sourceValues[source];
      if (modifiedAttributes[attrId] && modifiedAttributes[attrId][source]) {
        value = modifiedAttributes[attrId][source];
      }
      onAttributeChange(aa.attributeId, value);
    } else {
      resetAttribute(aa.attributeId, `${aa.currentValue}`);
    }
  };

  const renderAttributeTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell scope="col" align="center" className={classes.columnHeader}>
          {t("labels.record.augmentation.dialog.attributeName")}
        </TableCell>
        <TableCell
          scope="col"
          align="center"
          className={classes.contentDividerHeader}
        >
          {t("labels.record.augmentation.dialog.currentValue")}
        </TableCell>
        {sourceNames.map((sourceName, idx) => (
          <React.Fragment key={idx}>
            <TableCell
              scope="col"
              align="center"
              className={classes.contentDividerHeader}
            >
              <LongText
                text={sourceName}
                width={sourceNames.length > 2 ? "200px" : "100%"}
              />
            </TableCell>
            <TableCell
              scope="col"
              align="center"
              className={classes.actionColumnHeader}
            >
              <LongText
                text={t("labels.record.augmentation.dialog.selection")}
                width={"50px"}
              />
            </TableCell>
          </React.Fragment>
        ))}
      </TableRow>
    </TableHead>
  );

  const renderAttributeTableContent = () => {
    return (
      <TableBody>
        {augmentableAttributes.map((attr, idx) => (
          <TableRow key={idx}>
            <TableCell align="center" className={classes.fieldValue}>
              <Typography component="span">
                <LongText text={attr.name} width={"100%"} />
              </Typography>
            </TableCell>
            <TableCell align="center" className={classes.contentDividerCell}>
              <Typography component="span">{renderAttrValue(attr)}</Typography>
            </TableCell>
            {sourceNames.map((sourceName, idx) => (
              <React.Fragment key={idx}>
                <TableCell
                  align="center"
                  className={classes.contentDividerCell}
                >
                  <Typography component="span">
                    {renderSourceAttrValue(attr, sourceName)}
                  </Typography>
                </TableCell>
                <TableCell align="center" className={classes.fieldValue}>
                  {attr.sourceValues[sourceName] ? (
                    attr.dataType === "CR" ? (
                      <CheckBox
                        checked={isUnitCheckboxChecked(attr, sourceName)}
                        onChange={(e) =>
                          selectSource(
                            e.target.checked,
                            attr.attributeId,
                            sourceName,
                          )
                        }
                        size="small"
                        color="primary"
                        style={{ padding: 0, marginRight: 5 }}
                        disabled={isUnitCheckboxDisabled(attr, sourceName)}
                      />
                    ) : (
                      <CheckBox
                        checked={isCheckboxChecked(attr, sourceName)}
                        onChange={(e) =>
                          selectSource(
                            e.target.checked,
                            attr.attributeId,
                            sourceName,
                          )
                        }
                        size="small"
                        color="primary"
                        style={{ padding: 0, marginRight: 5 }}
                        disabled={isCheckboxDisabled(attr, sourceName)}
                      />
                    )
                  ) : (
                    ""
                  )}
                </TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderAttributeTable = () => {
    return (
      <>
        <Card>
          <TableContainer>
            <Table className={classes.table} size="small">
              {renderAttributeTableHeader()}
              {renderAttributeTableContent()}
            </Table>
          </TableContainer>
          <CardActions className={classes.cardActionButton}>
            <LightTooltip
              title={
                <span>
                  {t("messages.record.augmentation.updateAttributes")}
                </span>
              }
              placement="left-start"
              style={{ padding: "0.5rem" }}
            >
              <span>
                <Button
                  label={t("labels.common.save")}
                  aria-label={t("labels.common.save")}
                  onClick={saveAttrChanges}
                  disableElevation
                  disabled={
                    isUpdateOperating ||
                    augmentableAttributes.filter(
                      (a) => a.selectedSource !== "current",
                    ).length === 0
                  }
                  size="small"
                  color="primary"
                >
                  {isUpdateOperating
                    ? renderButtonLoadingIndicator()
                    : t("labels.common.save")}
                </Button>
              </span>
            </LightTooltip>
          </CardActions>
        </Card>
      </>
    );
  };

  return hasAccess ? (
    loading ? (
      renderLoadingIndicator()
    ) : (
      renderAttributeTable()
    )
  ) : (
    <div style={{ position: "relative" }}>
      <NoAccessScreen />
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(
  forwardRef(AttributesTable),
);
